import { baseApi, RtkqRequest, rtkq, Http, ListSegment } from '@app/base';
import {
  CreateOrganizationRequest,
  Organization,
  UpdateOrganizationRequest,
  UpdateTwoFactorLoginRequiredRequest,
} from './organizationTypes';
import { GetOrganizationDocument, GetOrganizationsListSegmentDocument } from './graphql';
import { FilterRule, PagingRule, SortRule } from '@webinex/asky';

export const organizationsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOrganization: builder.query<Organization, RtkqRequest<{ id: string }>>({
      queryFn: rtkq.graphql(GetOrganizationDocument, {
        select: (x) => x.organization as Organization,
        vars: ({ id }) => ({ id }),
      }),
      providesTags: (result) => [{ type: 'main:organization', id: result?.id ?? 'none' }],
    }),

    getOrganizationsListSegment: builder.query<
      ListSegment<Organization>,
      RtkqRequest<{
        searchValue?: string | null;
        filterRule?: FilterRule | null;
        sortRule?: SortRule | null;
        pagingRule?: PagingRule | null;
      }>
    >({
      queryFn: rtkq.graphql(GetOrganizationsListSegmentDocument, {
        select: (x) => x.organizationsListSegment!,
        vars: ({ searchValue, ...query }) => ({
          query,
          searchValue: searchValue ?? null,
        }),
      }),
      providesTags: (result = { items: [], total: 0 }) => [
        { type: 'main:organization', id: 'list' },
        ...result.items.map(({ id }) => ({ type: 'main:organization', id })),
      ],
    }),

    createOrganization: builder.mutation<string, RtkqRequest<CreateOrganizationRequest>>({
      queryFn: rtkq.axios((args) => Http.post(`/api/organization`, args)),
      invalidatesTags: [{ type: 'main:organization', id: 'list' }],
    }),

    updateOrganization: builder.mutation<void, RtkqRequest<UpdateOrganizationRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/organization/${args.id}`, args)),
      invalidatesTags: (_1, _2, { id }) => [{ type: 'main:organization', id }],
    }),

    updateTwoFactorLoginRequired: builder.mutation<void, RtkqRequest<UpdateTwoFactorLoginRequiredRequest>>({
      queryFn: rtkq.axios((args) => Http.put(`/api/organization/${args.id}/two-factor-login-required`, args)),
      invalidatesTags: (_1, _2, { id }) => [{ type: 'main:organization', id }],
    }),
  }),
});

export const {
  useGetOrganizationQuery,
  useLazyGetOrganizationQuery,
  useGetOrganizationsListSegmentQuery,
  useCreateOrganizationMutation,
  useUpdateOrganizationMutation,
  useUpdateTwoFactorLoginRequiredMutation,
} = organizationsApi;
